import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, History, Upload, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Card,
  KordaBadge,
  KorwilBadge,
  SectionTitle,
  StatusBadge,
  TableShell,
  Td,
  Th,
  fmtDate,
} from "@/components/Shared";

const ROW_TONE = {
  BARU: "text-emerald-300",
  UPDATE: "text-slate-500",
  DITOLAK_SUDAH_ADA: "text-red-300",
  MILIK_KORDA_LAIN: "text-red-300",
  MILIK_KORWIL_LAIN: "text-red-300",
  MILIK_SCOPE_LAIN: "text-red-300",
  TEKNISI_TIDAK_DIKENAL: "text-amber-300",
};

export default function UploadAlokasi() {
  const { user } = useAuth();
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [dummyBusy, setDummyBusy] = useState(false);
  const [dummyTotal, setDummyTotal] = useState(null);

  const loadHistory = useCallback(() => {
    api
      .get("/import/history")
      .then(({ data }) => setHistory(data.items))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const loadDummyStatus = useCallback(() => {
    if (user?.role !== "SUPER_ADMIN") return;
    api.get("/training/dummy")
      .then(({ data }) => setDummyTotal(data.total))
      .catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    loadDummyStatus();
  }, [loadDummyStatus]);

  const generateDummy = async () => {
    if (!window.confirm("Buat data dummy training untuk semua teknisi aktif di DEV?")) return;
    setDummyBusy(true);
    try {
      const { data } = await api.post("/training/dummy/generate");
      setDummyTotal(data.total);
      toast.success(data.message || `Data training berhasil dibuat: ${data.total} jadwal`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setDummyBusy(false);
    }
  };

  const deleteDummy = async () => {
    if (!window.confirm("Hapus SEMUA data dummy training? Data operasional lain tidak akan dihapus.")) return;
    setDummyBusy(true);
    try {
      const { data } = await api.delete("/training/dummy");
      setDummyTotal(0);
      toast.success(`Data dummy training dihapus: ${data.deleted} jadwal`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setDummyBusy(false);
    }
  };

  const doPreview = async (f) => {
    setFile(f);
    setPreview(null);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/import/preview", fd);
      setPreview(data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/jadwal", fd);
      toast.success(
        `Impor selesai — ${data.inserted} baru, ${data.ditolak_sudah_ada || 0} ditolak karena ID sudah ada, ${data.duplikat} duplikat di file, ${data.skipped} dilewati`
      );
      setPreview(null);
      setFile(null);
      loadHistory();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-upload-alokasi">
      {user?.role === "SUPER_ADMIN" && (
        <Card className="mb-6 border-amber-800/60 bg-amber-950/20 p-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="mr-auto">
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-200">
                <Sparkles className="h-4 w-4" /> Data Dummy Training DEV
              </div>
              <div className="mt-1 text-xs text-slate-400">
                1 jadwal untuk setiap teknisi aktif. Khusus DEV, tidak menyentuh data production.
                {dummyTotal !== null && (
                  <span className="ml-2 font-mono-data text-amber-300">({dummyTotal} jadwal dummy)</span>
                )}
              </div>
            </div>
            <button
              type="button"
              disabled={dummyBusy}
              onClick={generateDummy}
              className="flex items-center gap-2 rounded bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
            >
              <Sparkles className="h-4 w-4" /> Buat Data Training
            </button>
            <button
              type="button"
              disabled={dummyBusy || !dummyTotal}
              onClick={deleteDummy}
              className="flex items-center gap-2 rounded border border-red-800 px-4 py-2 text-sm font-semibold text-red-300 transition-colors hover:bg-red-950 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" /> Hapus Data Dummy
            </button>
          </div>
        </Card>
      )}

      <SectionTitle
        title="Upload Alokasi Jadwal Pusat"
        subtitle="Unggah file alokasi dari website PYX Pusat (Excel / CSV). Kunci unik: id_transaksi — 1 id_transaksi = 1 unit instalasi."
      />

      <Card className="mb-6 p-5">
        <input
          ref={fileRef}
          data-testid="alokasi-file-input"
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && doPreview(e.target.files[0])}
        />
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="btn-pilih-file"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="h-4 w-4" /> Pilih File Alokasi
          </button>
          <span className="font-mono-data text-xs text-slate-400" data-testid="alokasi-filename">
            {file ? file.name : "Belum ada file dipilih"}
          </span>
          <span className="ml-auto text-[11px] text-slate-500">
            Kolom wajib: <span className="font-mono-data text-slate-300">ID Transaksi</span> ·
            Korda/Korwil ditentukan otomatis dari <span className="font-mono-data text-slate-300">Kode Teknisi</span>
          </span>
        </div>
      </Card>

      {preview && (
        <Card className="mb-6 p-5" data-testid="alokasi-preview">
          <h3 className="mb-4 flex items-center gap-2 font-display text-base font-semibold text-white">
            <FileSpreadsheet className="h-4 w-4 text-blue-400" /> Pratinjau Sebelum Impor
          </h3>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Total Baris", preview.total_baris, "text-white", "preview-total"],
              ["Record Baru", preview.baru, "text-emerald-300", "preview-baru"],
              ["ID Sudah Ada (Ditolak)", preview.ditolak_sudah_ada || 0, "text-red-300", "preview-ditolak"],
              ["Duplikat di File", preview.duplikat_dalam_file, "text-amber-300", "preview-duplikat"],
              ["Baris Tidak Valid", preview.baris_tidak_valid, "text-red-300", "preview-invalid"],
            ].map(([l, v, c, tid]) => (
              <div key={l} className="rounded border border-slate-800 bg-slate-900/70 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div>
                <div data-testid={tid} className={`font-mono-data text-2xl font-semibold ${c}`}>
                  {v}
                </div>
              </div>
            ))}
          </div>

          <div className="mb-4 flex flex-wrap gap-2 text-[11px]">
            <span className="text-slate-500">Kolom terdeteksi:</span>
            {preview.kolom_terdeteksi.map((c) => (
              <span
                key={c}
                className="rounded border border-slate-700 bg-slate-900 px-1.5 py-0.5 font-mono-data text-slate-300"
              >
                {c}
              </span>
            ))}
          </div>

          {preview.milik_korda_lain > 0 && (
            <div className="mb-4 flex items-start gap-2 rounded border border-red-800 bg-red-950/50 p-3 text-xs text-red-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {preview.milik_korda_lain} baris berada di luar scope Anda (Korda/Korwil lain) dan
              akan dilewati (tidak ditimpa).
            </div>
          )}
          {preview.teknisi_tidak_dikenal > 0 && (
            <div className="mb-4 flex items-start gap-2 rounded border border-amber-800 bg-amber-950/40 p-3 text-xs text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              {preview.teknisi_tidak_dikenal} baris memiliki Kode Teknisi yang tidak ada di master
              teknisi, sehingga Korda/Korwil pemiliknya tidak dapat ditentukan — baris ini dilewati.
            </div>
          )}

          <div className="overflow-x-auto rounded border border-slate-800">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr>
                  <Th>Deteksi</Th>
                  <Th>id_transaksi</Th>
                  <Th>NPSN</Th>
                  <Th>Sekolah</Th>
                  <Th>Kabupaten</Th>
                  <Th>Unit</Th>
                  <Th>Korda</Th>
                  <Th>Korwil</Th>
                  <Th>Jadwal</Th>
                  <Th>Kode Teknisi</Th>
                  <Th>Teknisi Pusat</Th>
                </tr>
              </thead>
              <tbody>
                {preview.preview.map((r, i) => (
                  <tr key={`${r.id_transaksi}-${i}`} className="border-b border-slate-800/70">
                    <Td className={`text-[11px] font-semibold ${ROW_TONE[r._status]}`}>
                      {(r._status || "").replace(/_/g, " ")}
                    </Td>
                    <Td className="font-mono-data text-xs text-blue-300">{r.id_transaksi}</Td>
                    <Td className="font-mono-data text-xs">{r.npsn || "-"}</Td>
                    <Td className="max-w-[180px] truncate text-xs">{r.sekolah || "-"}</Td>
                    <Td className="text-xs">{r.kabupaten_kota || "-"}</Td>
                    <Td className="font-mono-data text-xs">{r.unit || "-"}</Td>
                    <Td>{r.korda ? <KordaBadge value={r.korda} /> : "-"}</Td>
                    <Td>{r.korwil ? <KorwilBadge value={r.korwil} /> : "-"}</Td>
                    <Td className="font-mono-data text-xs">{r.jadwal ? fmtDate(r.jadwal) : "-"}</Td>
                    <Td className="font-mono-data text-[11px] text-blue-300">
                      {r.teknisi_pusat_kode || "-"}
                    </Td>
                    <Td className="text-xs">
                      {r.teknisi_pusat_nama || <span className="text-slate-500">-</span>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              data-testid="btn-konfirmasi-impor"
              disabled={busy || preview.baru === 0}
              onClick={doImport}
              className="flex items-center gap-2 rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle2 className="h-4 w-4" /> Konfirmasi & Impor
            </button>
            <button
              data-testid="btn-batal-impor"
              onClick={() => {
                setPreview(null);
                setFile(null);
              }}
              className="rounded border border-slate-700 px-4 py-2 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
            >
              Batal
            </button>
          </div>
        </Card>
      )}

      <h3 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-white">
        <History className="h-4 w-4 text-slate-400" /> Riwayat Impor
      </h3>
      <TableShell testId="import-history-table" empty={history.length === 0}>
        <thead>
          <tr>
            <Th>Waktu</Th>
            <Th>File</Th>
            <Th>Diimpor Oleh</Th>
            <Th>Role</Th>
            <Th>Korda</Th>
            <Th className="text-right">Baris</Th>
            <Th className="text-right">Baru</Th>
            <Th className="text-right">Update</Th>
            <Th className="text-right">Ditolak</Th>
            <Th className="text-right">Duplikat</Th>
            <Th className="text-right">Dilewati</Th>
          </tr>
        </thead>
        <tbody>
          {history.map((h) => (
            <tr key={h.id} data-testid={`import-log-${h.id}`} className="border-b border-slate-800/70 hover:bg-slate-800/50">
              <Td className="font-mono-data text-xs">
                {new Date(h.created_at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}
              </Td>
              <Td className="max-w-[220px] truncate text-xs text-slate-100">{h.filename}</Td>
              <Td className="text-xs">{h.user_name}</Td>
              <Td><StatusBadge value={h.user_role} /></Td>
              <Td><KordaBadge value={h.korda} /></Td>
              <Td className="text-right font-mono-data text-xs">{h.total_baris}</Td>
              <Td className="text-right font-mono-data text-xs text-emerald-300">{h.inserted}</Td>
              <Td className="text-right font-mono-data text-xs text-slate-500">{h.updated || 0}</Td>
              <Td className="text-right font-mono-data text-xs text-red-300">{h.ditolak_sudah_ada || 0}</Td>
              <Td className="text-right font-mono-data text-xs text-amber-300">{h.duplikat}</Td>
              <Td className="text-right font-mono-data text-xs text-red-300">{h.skipped}</Td>
            </tr>
          ))}
        </tbody>
      </TableShell>
    </div>
  );
}
